import React from "react";
import { Link } from "react-router-dom";
import { AlertTriangle, MapPin } from "lucide-react";
import { rp } from "@/lib/api";

export const Card = ({ card }) => (
  <div data-testid={`dash-card-${card.key}`}
    className={`bg-white border rounded-lg p-4 ${
      card.tone === "danger" && card.value > 0 ? "border-lm-red/60" : "border-black/10"}`}>
    <div className="text-[10px] uppercase tracking-widest text-ink-soft">{card.label}</div>
    <div className={`mt-1.5 text-2xl font-semibold ${
      card.tone === "danger" && card.value > 0 ? "text-lm-red" : ""}`}>
      {card.money ? rp(card.value) : card.value}
    </div>
  </div>
);

export const Timeline = ({ rows = [] }) => (
  <section data-testid="dash-timeline">
    <h2 className="text-base md:text-lg font-semibold mb-3">Jadwal hari ini</h2>
    <div className="bg-white border border-black/10 rounded-lg divide-y divide-black/5">
      {rows.length === 0 && (
        <div className="p-4 text-sm text-ink-soft" data-testid="timeline-empty">
          Tidak ada jadwal hari ini.
        </div>
      )}
      {rows.map((t) => (
        <Link key={`${t.booking_id}-${t.group}`} to={t.link} data-testid={`timeline-${t.booking_code}`}
          className="flex items-center justify-between gap-4 p-3.5 hover:bg-cream-dark/40">
          <div className="min-w-0">
            <div className="text-sm font-medium truncate">
              {t.start_time || "--:--"} · {t.customer_name} · {t.booking_code}
            </div>
            <div className="text-[11px] text-ink-soft">
              {t.hub} · {t.service_label} · {t.package}
            </div>
          </div>
          <span className="shrink-0 text-[11px] px-2 py-1 rounded-full border border-black/15">
            {t.status}
          </span>
        </Link>
      ))}
    </div>
  </section>
);

export const FinancePanel = ({ fin }) => (
  <section data-testid="finance-panel" className="space-y-3">
    <h2 className="text-base md:text-lg font-semibold">Keuangan</h2>
    <div className="grid sm:grid-cols-2 gap-3">
      <div className="bg-white border border-black/10 rounded-lg p-4">
        <div className="text-[10px] uppercase tracking-widest text-ink-soft">Selisih pembayaran</div>
        {fin.mismatch.length === 0 && (
          <div className="mt-2 text-sm text-ink-soft" data-testid="finance-no-mismatch">
            Tidak ada selisih. Semua booking Lunas cocok dengan total tagihan.
          </div>
        )}
        <div className="mt-2 divide-y divide-black/5">
          {fin.mismatch.map((m) => (
            <Link key={m.booking_id} to={`/admin/bookings/${m.booking_id}`}
              data-testid={`finance-mismatch-${m.booking_code}`}
              className="flex items-center justify-between py-2 text-xs hover:text-lm-red">
              <span>{m.booking_code}</span>
              <span>{rp(m.paid)} / {rp(m.total)}</span>
            </Link>
          ))}
        </div>
      </div>
      <div className="bg-white border border-black/10 rounded-lg p-4 space-y-2">
        <div className="text-[10px] uppercase tracking-widest text-ink-soft">Ringkasan</div>
        <div className="flex justify-between text-sm"><span>Pembayaran masuk hari ini</span>
          <span className="font-semibold">{rp(fin.paid_today_amount)}</span></div>
        <div className="flex justify-between text-sm"><span>Transaksi gagal / kedaluwarsa</span>
          <span className="font-semibold">{fin.failed_attempts}</span></div>
        <div className="flex justify-between text-sm"><span>Deposit menunggu dikembalikan</span>
          <span className="font-semibold">{fin.deposits_to_release}</span></div>
        <div className="flex justify-between text-sm"><span>Komisi partner belum lunas</span>
          <span className="font-semibold">{rp(fin.commissions.pending)}</span></div>
        <div className="pt-2 flex gap-3 text-xs">
          <Link to="/admin/online-payments" data-testid="finance-goto-online"
            className="text-lm-red hover:underline">Pembayaran Online →</Link>
          <Link to="/admin/partner-commissions" data-testid="finance-goto-commissions"
            className="text-lm-red hover:underline">Komisi Partner →</Link>
        </div>
      </div>
    </div>
  </section>
);

export const HubConflicts = ({ rows = [] }) => {
  if (rows.length === 0) return null;
  return (
    <section data-testid="hub-conflicts"
      className="bg-white border border-lm-red/50 rounded-lg p-4">
      <div className="flex items-center gap-2">
        <AlertTriangle size={14} className="text-lm-red" />
        <h2 className="text-sm font-semibold">Booking bentrok dengan jadwal Hub ({rows.length})</h2>
      </div>
      <div className="mt-2 divide-y divide-black/5">
        {rows.map((c, i) => (
          <div key={`${c.booking_code}-${i}`} data-testid={`hub-conflict-${c.booking_code}`}
            className="py-2 text-xs flex flex-wrap items-center justify-between gap-2">
            <span>{c.hub} · {c.booking_code} · {c.rental_date}</span>
            <span className="text-lm-red">{c.conflict}</span>
          </div>
        ))}
      </div>
      <Link to="/admin/branches" data-testid="hub-conflicts-goto"
        className="mt-2 inline-block text-xs text-lm-red hover:underline">Buka Hub Management →</Link>
    </section>
  );
};

export const HubsOverview = ({ rows = [] }) => (
  <section data-testid="hubs-overview">
    <h2 className="text-base md:text-lg font-semibold mb-3">Hub</h2>
    <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
      {rows.map((h) => (
        <Link key={h.hub_id} to="/admin/branches" data-testid={`hub-tile-${h.hub_id}`}
          className="bg-white border border-black/10 rounded-lg p-4 hover:border-lm-red/50">
          <div className="flex items-center gap-1.5 text-sm font-medium">
            <MapPin size={13} className="text-ink-soft" /> {h.name}
          </div>
          <div className="mt-1 text-[11px] text-ink-soft">{h.status} · {h.units} unit</div>
          <div className={`mt-1 text-[11px] ${h.play_at_venue_enabled ? "text-emerald-700" : "text-ink-soft"}`}>
            Main di Venue {h.play_at_venue_enabled ? "aktif" : "mati"}
          </div>
        </Link>
      ))}
    </div>
  </section>
);

export const PartnerPanel = ({ hubs = [], commissions = { pending: 0, earned: 0 }, percent = 0 }) => (
  <section data-testid="partner-panel" className="grid sm:grid-cols-2 gap-3">
    <div className="bg-white border border-black/10 rounded-lg p-4">
      <div className="text-[10px] uppercase tracking-widest text-ink-soft">Hub Anda</div>
      <div className="mt-2 space-y-1.5">
        {hubs.map((h) => (
          <div key={h.hub_id} data-testid={`partner-hub-${h.hub_id}`} className="text-sm">
            {h.name}
            <span className="ml-2 text-[11px] text-ink-soft">
              Main di Venue {h.play_at_venue_enabled ? "aktif" : "mati"}
            </span>
          </div>
        ))}
        {hubs.length === 0 && (
          <div className="text-sm text-ink-soft" data-testid="partner-no-hub">
            Akun Anda belum ditautkan ke Hub. Minta owner mengaturnya di Staff Accounts.
          </div>
        )}
      </div>
      <Link to="/admin/partner" data-testid="partner-goto-hub"
        className="mt-3 inline-block text-xs text-lm-red hover:underline">+ Booking di Venue →</Link>
    </div>
    <div className="bg-white border border-black/10 rounded-lg p-4 space-y-2">
      <div className="text-[10px] uppercase tracking-widest text-ink-soft">Komisi saya</div>
      <div className="flex justify-between text-sm"><span>Belum lunas</span>
        <span className="font-semibold" data-testid="partner-commission-pending">{rp(commissions.pending)}</span></div>
      <div className="flex justify-between text-sm"><span>Sudah jadi hak</span>
        <span className="font-semibold" data-testid="partner-commission-earned">{rp(commissions.earned)}</span></div>
      <div className="text-[11px] text-ink-soft">Persentase komisi aktif: {percent}%</div>
    </div>
  </section>
);
